(() => {
  'use strict';

  const STORE_PREFIX = 'minaco-account';
  const safeParse = (value, fallback = []) => {
    try { return JSON.parse(value) ?? fallback; } catch (_) { return fallback; }
  };

  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const safeStorageGet = (key) => {
    try { return localStorage.getItem(key); } catch (_) { return null; }
  };

  const safeStorageSet = (key, value) => {
    try { localStorage.setItem(key, value); return true; } catch (_) { return false; }
  };

  const normalizeRecord = (source = {}) => ({
    id: String(source.id || ''),
    handle: String(source.handle || ''),
    title: String(source.title || ''),
    url: String(source.url || ''),
    image: String(source.image || ''),
    price: String(source.price || ''),
    subtitle: String(source.subtitle || ''),
    viewedAt: Number(source.viewedAt || Date.now()),
  });

  const recordKey = (record) => record.id || record.handle || record.url;

  const init = (root) => {
    if (!root || root.dataset.minacoAccountInitialized === 'true') return;
    root.dataset.minacoAccountInitialized = 'true';

    const loggedIn = root.dataset.loggedIn === 'true';
    const customerId = root.dataset.customerId || 'guest';
    const loginUrl = root.dataset.loginUrl || '/account/login';
    const drawer = root.querySelector('[data-minaco-account-drawer]');
    const panel = root.querySelector('[data-minaco-account-panel]');
    const scrollArea = root.querySelector('[data-minaco-account-scroll]');
    const tabs = [...root.querySelectorAll('[data-minaco-account-tab]')];
    const views = [...root.querySelectorAll('[data-minaco-account-view]')];
    const recentWrap = root.querySelector('[data-minaco-recent-products]');
    const recentEmpty = root.querySelector('[data-minaco-recent-empty]');
    const savedWrap = root.querySelector('[data-minaco-saved-products]');
    const savedEmpty = root.querySelector('[data-minaco-saved-empty]');
    const savedMore = root.querySelector('[data-minaco-saved-view-more]');
    const abortController = new AbortController();
    const listenerOptions = { signal: abortController.signal };
    let lastOpener = null;

    const storageKey = (kind) => `${STORE_PREFIX}:${kind}:${customerId}`;
    const readList = (kind) => safeParse(safeStorageGet(storageKey(kind)), []);
    const writeList = (kind, list) => safeStorageSet(storageKey(kind), JSON.stringify(list));

    const dedupe = (records, limit = 50) => {
      const seen = new Set();
      return records.filter((item) => {
        const key = recordKey(item);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      }).slice(0, limit);
    };

    const getWishlist = () => readList('saved').map(normalizeRecord);
    const setWishlist = (records) => {
      const list = dedupe(records.map(normalizeRecord), 60);
      writeList('saved', list);
      renderSaved();
      syncWishlistButtons();
      document.dispatchEvent(new CustomEvent('minaco:wishlist:changed', { detail: { items: list } }));
      return list;
    };

    const getRecent = () => readList('recent').map(normalizeRecord);
    const setRecent = (records) => {
      const list = dedupe(records.map(normalizeRecord), 16);
      writeList('recent', list);
      renderRecent();
      return list;
    };

    const productFromButton = (button) => normalizeRecord({
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      subtitle: button.dataset.productSubtitle,
    });

    const isSaved = (record) => {
      const key = recordKey(record);
      return Boolean(key && getWishlist().some((item) => recordKey(item) === key));
    };

    const syncButton = (button) => {
      if (!button) return;
      const saved = loggedIn && isSaved(productFromButton(button));
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      const addLabel = button.dataset.saveLabel || 'Save product';
      const removeLabel = button.dataset.savedLabel || 'Remove saved product';
      button.setAttribute('aria-label', saved ? removeLabel : addLabel);
      button.title = saved ? removeLabel : addLabel;
    };

    const syncWishlistButtons = (scope = document) => {
      scope.querySelectorAll('[data-minaco-wishlist-toggle]').forEach(syncButton);
    };

    const toggleWishlist = (button) => {
      if (!loggedIn) {
        window.location.assign(button.dataset.loginUrl || loginUrl);
        return false;
      }

      const record = productFromButton(button);
      const key = recordKey(record);
      if (!key) return false;
      const current = getWishlist();
      const existing = current.findIndex((item) => recordKey(item) === key);
      if (existing >= 0) current.splice(existing, 1);
      else current.unshift(record);
      setWishlist(current);
      return existing < 0;
    };

    const cardMarkup = (record, { removable = false } = {}) => {
      const key = recordKey(record);
      return `
        <article class="minaco-account-card minaco-account-card--dynamic" data-record-key="${escapeHtml(key)}">
          ${removable ? `<button type="button" class="minaco-account-card__remove" data-minaco-remove-saved="${escapeHtml(key)}" aria-label="Remove ${escapeHtml(record.title)}"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button>` : ''}
          <a class="minaco-account-card__media" href="${escapeHtml(record.url)}">
            ${record.image ? `<img src="${escapeHtml(record.image)}" alt="${escapeHtml(record.title)}" loading="lazy">` : ''}
          </a>
          <div class="minaco-account-card__meta">
            <a class="minaco-account-card__title" href="${escapeHtml(record.url)}">${escapeHtml(record.title)}</a>
            <div class="minaco-account-card__price">${escapeHtml(record.price)}</div>
          </div>
        </article>`;
    };

    const renderRecent = () => {
      if (!recentWrap) return;
      const items = getRecent().slice(0, 2);
      recentWrap.innerHTML = items.map((item) => cardMarkup(item)).join('');
      if (recentEmpty) recentEmpty.hidden = items.length > 0;
    };

    const renderSaved = () => {
      if (!savedWrap) return;
      const items = getWishlist();
      savedWrap.innerHTML = items.slice(0, 2).map((item) => cardMarkup(item, { removable: true })).join('');
      if (savedEmpty) savedEmpty.hidden = items.length > 0;
      if (savedMore) savedMore.hidden = items.length <= 2;
    };

    const trackCurrentProduct = () => {
      if (!loggedIn) return;
      const productRoot = document.querySelector('minaco-product[data-product-handle], .minaco-product[data-product-handle]');
      if (!productRoot) return;
      const record = normalizeRecord({
        id: productRoot.dataset.productId,
        handle: productRoot.dataset.productHandle,
        title: productRoot.dataset.productTitle,
        url: productRoot.dataset.productUrl || window.location.pathname,
        image: productRoot.dataset.productImage,
        price: productRoot.dataset.productPrice,
        subtitle: productRoot.dataset.productSubtitle,
        viewedAt: Date.now(),
      });
      if (!recordKey(record)) return;
      setRecent([record, ...getRecent().filter((item) => recordKey(item) !== recordKey(record))]);
    };

    const showView = (name) => {
      views.forEach((view) => {
        const active = view.dataset.minacoAccountView === name;
        view.hidden = !active;
        view.classList.toggle('is-active', active);
      });
      tabs.forEach((tab) => {
        const active = tab.dataset.minacoAccountTab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      if (scrollArea) scrollArea.scrollTop = 0;
    };

    const open = (opener = null, view = 'for-you') => {
      if (!loggedIn || !drawer) return;
      lastOpener = opener || document.activeElement;
      renderRecent();
      renderSaved();
      syncWishlistButtons();
      showView(view);
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('minaco-account-open');
      requestAnimationFrame(() => panel?.focus({ preventScroll: true }));
    };

    const close = () => {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('minaco-account-open');
      window.setTimeout(() => lastOpener?.focus?.({ preventScroll: true }), 120);
    };

    tabs.forEach((tab) => tab.addEventListener('click', () => showView(tab.dataset.minacoAccountTab), listenerOptions));
    root.querySelectorAll('[data-minaco-account-close]').forEach((button) => button.addEventListener('click', close, listenerOptions));

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-minaco-account-open]');
      if (opener) {
        if (loggedIn) {
          event.preventDefault();
          open(opener, opener.dataset.accountView || 'for-you');
        }
        return;
      }

      const saveButton = event.target.closest('[data-minaco-wishlist-toggle]');
      if (saveButton) {
        event.preventDefault();
        event.stopPropagation();
        toggleWishlist(saveButton);
        return;
      }

      const remove = event.target.closest('[data-minaco-remove-saved]');
      if (remove && loggedIn) {
        event.preventDefault();
        const key = remove.dataset.minacoRemoveSaved;
        setWishlist(getWishlist().filter((item) => recordKey(item) !== key));
      }
    }, listenerOptions);

    document.addEventListener('keydown', (event) => {
      if (!drawer?.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, listenerOptions);

    document.addEventListener('shopify:section:unload', (event) => {
      const sectionElement = event.target;
      if (sectionElement === root || sectionElement?.contains?.(root)) {
        abortController.abort();
        if (drawer?.classList.contains('is-open')) document.body.classList.remove('minaco-account-open');
      }
    }, listenerOptions);

    renderRecent();
    renderSaved();
    trackCurrentProduct();
    syncWishlistButtons();

    window.MinacoAccount = {
      ...(window.MinacoAccount || {}),
      open,
      close,
      refresh: () => { renderRecent(); renderSaved(); syncWishlistButtons(); },
      syncButton,
      getWishlist,
      toggleWishlist,
      loggedIn,
    };
  };

  const renderSavedPage = () => {
    const page = document.querySelector('[data-minaco-saved-page]');
    const root = document.querySelector('[data-minaco-account-root]');
    if (!page || !root) return;

    const grid = page.querySelector('[data-minaco-saved-page-grid]');
    const empty = page.querySelector('[data-minaco-saved-page-empty]');
    const login = page.querySelector('[data-minaco-saved-page-login]');
    const loggedIn = root.dataset.loggedIn === 'true';
    const customerId = root.dataset.customerId || 'guest';

    if (!loggedIn) {
      if (grid) grid.replaceChildren();
      if (login) login.hidden = false;
      if (empty) empty.hidden = true;
      return;
    }

    const key = `${STORE_PREFIX}:saved:${customerId}`;
    const items = safeParse(safeStorageGet(key), []).map(normalizeRecord);
    if (grid) {
      grid.innerHTML = items.map((record) => {
        const itemKey = escapeHtml(recordKey(record));
        const title = escapeHtml(record.title);
        const url = escapeHtml(record.url);
        const image = escapeHtml(record.image);
        const price = escapeHtml(record.price);
        const id = escapeHtml(record.id);
        const handle = escapeHtml(record.handle);
        const subtitle = escapeHtml(record.subtitle);
        return `
          <article class="minaco-account-card minaco-account-card--dynamic" data-record-key="${itemKey}">
            <button type="button" class="minaco-account-card__remove" data-minaco-wishlist-toggle data-product-id="${id}" data-product-handle="${handle}" data-product-title="${title}" data-product-url="${url}" data-product-image="${image}" data-product-price="${price}" data-product-subtitle="${subtitle}" aria-pressed="true" aria-label="Remove saved product">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            </button>
            <a class="minaco-account-card__media" href="${url}">${image ? `<img src="${image}" alt="${title}" loading="lazy">` : ''}</a>
            <div class="minaco-account-card__meta">
              <a class="minaco-account-card__title" href="${url}">${title}</a>
              <div class="minaco-account-card__price">${price}</div>
            </div>
          </article>`;
      }).join('');
    }
    if (empty) empty.hidden = items.length > 0;
    if (login) login.hidden = true;
  };

  const initialize = () => {
    document.querySelectorAll('[data-minaco-account-root]').forEach(init);
    renderSavedPage();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('minaco:wishlist:changed', renderSavedPage);
})();
